import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader } from "../components/Bits";
import { SupplierFormDialog, SupplierLedgerDialog, fmtDay } from "../components/SupplierDialogs";
import { api, errMsg, money } from "../lib/api";

const PurchaseDetail = ({ p, onClose, onDeleted }) => {
  const del = async () => {
    if (!window.confirm(`${p.purchase_no} silinsin mi? Stok girişleri geri alınır.`)) return;
    try { await api.delete(`/purchases/${p.id}`); toast.success("Alış faturası silindi, stok geri alındı"); onDeleted(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-1.5rem)] max-w-2xl rounded-xl bg-white max-h-[90vh] overflow-y-auto" data-testid="purchase-detail-dialog">
        {p && <>
          <DialogHeader><DialogTitle className="font-display">{p.purchase_no} · {p.supplier_name}</DialogTitle>
            <p className="text-sm text-slate-500">Fatura No: {p.invoice_no || "-"} · Tarih: {fmtDay(p.invoice_date)}</p></DialogHeader>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase text-slate-500"><tr><th className="p-2">Ürün</th><th className="p-2 text-right">Miktar</th><th className="p-2 text-right">Alış Fiyatı</th><th className="p-2 text-right">KDV</th><th className="p-2 text-right">Tutar</th></tr></thead>
              <tbody>{p.items.map((i) => (
                <tr key={i.product_id} className="border-t border-slate-100"><td className="p-2"><b>{i.name}</b><div className="font-mono text-[11px] text-slate-500">{i.code}</div></td>
                  <td className="p-2 text-right">{i.quantity} {i.unit}</td><td className="p-2 text-right">{money(i.unit_cost)}</td><td className="p-2 text-right">%{i.vat}</td><td className="p-2 text-right font-semibold">{money(i.net)}</td></tr>))}</tbody>
            </table>
          </div>
          <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between text-slate-600"><span>Ara Toplam</span><span>{money(p.subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>KDV</span><span>{money(p.vat_total)}</span></div>
            <div className="flex justify-between border-t pt-2 font-display text-lg font-bold"><span>Toplam</span><span data-testid="purchase-detail-total">{money(p.grand_total)}</span></div>
          </div>
          {p.note && <p className="text-sm text-slate-600"><b>Not:</b> {p.note}</p>}
          <Button data-testid="purchase-delete-button" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={del}><Trash2 className="mr-2 h-4 w-4" />Alış Faturasını Sil</Button>
        </>}
      </DialogContent>
    </Dialog>
  );
};

const PurchasesTab = ({ purchases, onOpen }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">
        <tr><th className="p-4">Alış No</th><th className="p-4">Tarih</th><th className="p-4">Tedarikçi</th><th className="p-4">Fatura No</th><th className="p-4 text-right">Tutar</th></tr>
      </thead>
      <tbody>
        {purchases.length === 0 && <tr><td colSpan={5} className="p-12 text-center text-slate-500" data-testid="purchases-empty">Alış faturası yok</td></tr>}
        {purchases.map((p) => (
          <tr key={p.id} data-testid={`purchase-row-${p.purchase_no}`} onClick={() => onOpen(p)} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-blue-50/50">
            <td className="p-4 font-mono font-semibold whitespace-nowrap">{p.purchase_no}</td><td className="p-4 whitespace-nowrap text-slate-500">{fmtDay(p.invoice_date)}</td>
            <td className="p-4 font-medium">{p.supplier_name}</td><td className="p-4 text-slate-600">{p.invoice_no || "-"}</td>
            <td className="p-4 text-right font-display font-bold whitespace-nowrap">{money(p.grand_total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SuppliersTab = ({ suppliers, onOpen, onEdit, onDelete }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">
        <tr><th className="p-4">Tedarikçi</th><th className="p-4">Telefon</th><th className="p-4">Vergi D. / No</th><th className="p-4 text-right">Bakiye (Borcumuz)</th><th className="p-4" /></tr>
      </thead>
      <tbody>
        {suppliers.length === 0 && <tr><td colSpan={5} className="p-12 text-center text-slate-500" data-testid="suppliers-empty">Tedarikçi yok</td></tr>}
        {suppliers.map((s) => (
          <tr key={s.id} data-testid={`supplier-row-${s.id}`} onClick={() => onOpen(s)} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-blue-50/50">
            <td className="p-4 font-semibold">{s.name}</td><td className="p-4 text-slate-600 whitespace-nowrap">{s.phone || "-"}</td>
            <td className="p-4 text-slate-600">{[s.tax_office, s.tax_no].filter(Boolean).join(" / ") || "-"}</td>
            <td className={`p-4 text-right font-display font-bold whitespace-nowrap ${s.balance > 0.005 ? "text-rose-700" : "text-slate-400"}`} data-testid={`supplier-balance-${s.id}`}>{money(s.balance)}</td>
            <td className="p-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
              <button data-testid={`supplier-edit-${s.id}`} onClick={() => onEdit(s)} className="mr-1 inline-grid h-8 w-8 place-items-center rounded-lg border border-slate-200 hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" /></button>
              <button data-testid={`supplier-delete-${s.id}`} onClick={() => onDelete(s)} className="inline-grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PurchasesPage() {
  const nav = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [edit, setEdit] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const load = () => {
    api.get("/purchases").then((r) => setPurchases(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, []);
  const s = q.toLocaleLowerCase("tr");
  const fp = useMemo(() => purchases.filter((p) => !s || [p.purchase_no, p.supplier_name, p.invoice_no].some((v) => (v || "").toLocaleLowerCase("tr").includes(s))), [purchases, s]);
  const fs = useMemo(() => suppliers.filter((x) => !s || [x.name, x.phone, x.tax_no].some((v) => (v || "").toLocaleLowerCase("tr").includes(s))), [suppliers, s]);
  const totalDebt = suppliers.reduce((a, x) => a + x.balance, 0);
  const del = async (x) => {
    if (!window.confirm(`"${x.name}" silinsin mi?`)) return;
    try { await api.delete(`/suppliers/${x.id}`); toast.success("Tedarikçi silindi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Alışlar" kicker={`${purchases.length} alış faturası · ${suppliers.length} tedarikçi`}>
        <Button data-testid="suppliers-add-button" variant="outline" onClick={() => { setEdit(null); setFormOpen(true); }}><Plus className="mr-2 h-4 w-4" />Yeni Tedarikçi</Button>
        <Button data-testid="purchases-new-button" className="bg-slate-950 hover:bg-blue-600" onClick={() => nav("/alislar/yeni")}><Plus className="mr-2 h-4 w-4" />Yeni Alış Faturası</Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3 fade-up">
        <div className="rounded-xl border border-slate-950 bg-slate-950 p-5 text-white sm:col-span-2">
          <p className="font-mono text-[11px] uppercase tracking-wider text-cyan-300">Tedarikçilere Toplam Borç</p>
          <p className="mt-2 font-display text-3xl font-bold" data-testid="suppliers-total-balance">{money(totalDebt)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Toplam Alış</p>
          <p className="mt-2 font-display text-3xl font-bold" data-testid="purchases-total">{money(purchases.reduce((a, p) => a + p.grand_total, 0))}</p>
        </div>
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="purchases-search-input" placeholder="Alış no, tedarikçi, fatura no ara..." value={q} onChange={(e) => setQ(e.target.value)} className="h-11 bg-white pl-10" />
      </div>
      <Tabs defaultValue="purchases">
        <TabsList className="mb-4">
          <TabsTrigger value="purchases" data-testid="tab-purchases">Alış Faturaları</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Tedarikçiler</TabsTrigger>
        </TabsList>
        <TabsContent value="purchases"><PurchasesTab purchases={fp} onOpen={setSel} /></TabsContent>
        <TabsContent value="suppliers"><SuppliersTab suppliers={fs} onOpen={setLedger} onEdit={(x) => { setEdit(x); setFormOpen(true); }} onDelete={del} /></TabsContent>
      </Tabs>
      <PurchaseDetail p={sel} onClose={() => setSel(null)} onDeleted={() => { setSel(null); load(); }} />
      <SupplierLedgerDialog supplier={ledger} onClose={() => setLedger(null)} onChanged={load} />
      <SupplierFormDialog open={formOpen} onOpenChange={setFormOpen} supplier={edit} onSaved={load} />
    </div>
  );
}
